import { useState } from 'react'
import { getConfig } from '@framework/api/config'
import { Collection } from '@common/types/collection'
import { Product } from '@common/types/product'
import { getCollection } from '@framework/product'
import SEO from '@components/common/SEO/SEO'
import s from './CollectionView.module.scss'
import ProductCard from '@components/product/ProductCard'
import { Pagination as PaginationType } from '@common/types/pagination'

import {
  Column,
  Grid,
  Searchbar,
  Breadcrumb,
  Pagination,
} from '@components/common'
import { Link } from '@components/ui'
import Close from '@components/icons/Close'
import useMedia from '@hooks/useMedia'
import useMenu from '@framework/menu/use-menu'

interface CollectionsViewProps {
  collectionSlug: string
  collection: Collection
  productPagination: PaginationType
}

// TODO: Add Typing
const FilterList = ({ filters }: any) => {
  return (
    <div className={s['filter-wrap']}>
      {filters.map((filter: any, i: number) => (
        <Link key={i} href={filter.href} className={s['filter-btn']}>
          {filter.text}
        </Link>
      ))}
    </div>
  )
}

// TODO: Typing
const MobileFilterList = ({ filters, setModalActive, title }: any) => {
  return (
    <div className={s['mobile-filter-modal']}>
      <div className={s['mobile-filter-header-row']}>
        <div className={s['filter-header']}>{title}</div>
        <div
          className={s['close-wrap']}
          onClick={() => {
            setModalActive(false)
          }}
        >
          <Close />
        </div>
      </div>
      <div>
        <Searchbar className={s['search-bar']} />
        <div className={s.divider}></div>
      </div>
      <div className={s['mobile-filter-wrap']}>
        {filters.map((filter: any, i: number) => (
          <div key={i} className={s['mobile-filter-btn-wrap']}>
            <Link href={filter.href} className={s['mobile-filter-btn']}>
              {filter.text}
            </Link>
          </div>
        ))}
      </div>
    </div>
  )
}

const CollectionsView = ({
  collection,
  productPagination,
}: CollectionsViewProps) => {
  const [modalActive, setModalActive] = useState(false)
  const [collectionProducts, setCollectionProducts] = useState(
    collection.products
  )
  const [pagination, setPagination] = useState(productPagination)
  const [loading, setLoading] = useState(false)
  const { data: menuData } = useMenu()
  const isDesktop = useMedia(
    // Media queries
    ['(min-width: 768px)'],
    // Column counts (relates to above media queries by array index)
    [true],
    // Default column count
    false
  )

  // TODO: Add typing
  const categoriesMenu = menuData?.find((menu: any) => {
    if (menu.handle === 'categories') {
      return true
    }
  })

  // TODO: typing
  const categoryMenu = categoriesMenu?.links?.find((menu: any) => {
    if (menu.text === collection.title) {
      return true
    }
  })

  const subCats = categoryMenu?.links ?? []

  const breadcrumbs = [
    {
      title: 'SHOP',
      link: '/',
    },
    {
      title: collection.title,
    },
  ]

  const loadMoreProducts = async () => {
    const config = getConfig()

    setLoading(true)

    try {
      const { collection: newCollection, productPagination } =
        await getCollection({
          config,
          variables: {
            slug: collection.slug,
            productCursor: pagination.endCursor,
            productPageCount: 48,
          },
        })

      setPagination(productPagination)

      if (newCollection && newCollection.products.length > 0) {
        setCollectionProducts([
          ...collectionProducts,
          ...newCollection.products,
        ])
      }

      setLoading(false)
    } catch (error) {
      setLoading(false)
      throw error
    }
  }

  //console.log(collection);

  return (
    <>
    <section className={s.root}>
      <div className={s['name-wrap']}>
        <Breadcrumb crumbs={breadcrumbs} />
        <h1 className={s.name}>{collection.title}</h1>
      </div>
      {isDesktop && <FilterList filters={subCats} />}
      <div
        onClick={() => {
          setModalActive(!modalActive)
        }}
        className={s['mobile-filter-button']}
      >
        FILTER
      </div>
      {!isDesktop && modalActive && (
        <MobileFilterList
          filters={subCats}
          setModalActive={setModalActive}
          title={collection.title}
        />
      )}

      <Grid smGutter={10} lgGutter={30} xsGutter={20}>
        {collectionProducts.length === 0 && (
          <div className={s['no-products-available']}>
            There are no products currently in this category.
          </div>
        )}
        {collectionProducts.length > 0 &&
          collectionProducts.map(
            (product: Product) =>
              product && (
                <Column sm={6} lg={4} key={product.name}>
                  <ProductCard product={product} />
                </Column>
              )
          )}
      </Grid>
      <div className={s['pagination-wrapper']}>
        <Pagination
          loadNextPage={() => {
            loadMoreProducts()
          }}
          hasNextPage={pagination.hasNextPage}
          loadingNextPage={loading}
        />
      </div>
    </section>
    <SEO
      title={collection.seo?.title || collection.title}
      description={collection.description || `Shop ${collection.title} from TS Pool Supply.`}
      openGraph={{
        type: 'website',
        title: collection.seo?.title || collection.title,
        description: collection.description || `Shop ${collection.title} from TS Pool Supply.`,
        images: [
          {
            url: '/static_images/ts-pool-supply-og-pool.jpg',
            width: 1280,
            height: 720,
            alt: 'TS Pool Supply',
          },
        ],
      }}
    />
    </>
  )
}

export default CollectionsView
